import { Link, useParams, Navigate } from "react-router-dom";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import Breadcrumbs from "@/components/Breadcrumbs";
import SEO from "@/components/SEO";
import useRevealOnScroll from "@/hooks/useRevealOnScroll";
import { ArrowLeft, ArrowUpRight } from "lucide-react";
import { insights, getInsightBySlug } from "@/data/insights";

function formatDate(iso) {
  try {
    return new Date(iso).toLocaleDateString("de-DE", {
      day: "2-digit",
      month: "long",
      year: "numeric",
    });
  } catch (e) {
    return iso;
  }
}

export default function InsightArticle() {
  useRevealOnScroll();
  const { slug } = useParams();
  const article = getInsightBySlug(slug);

  if (!article) {
    return <Navigate to="/insights" replace />;
  }

  const related = insights.filter((a) => a.slug !== article.slug).slice(0, 2);

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: article.title,
    description: article.excerpt,
    datePublished: article.date,
    inLanguage: "de-DE",
    author: { "@type": "Organization", name: "Wagenhofer Capital" },
    publisher: {
      "@type": "Organization",
      name: "Wagenhofer Capital",
      logo: {
        "@type": "ImageObject",
        url: "https://wagenhofer.capital/og-image.jpg",
      },
    },
    image: article.image,
    mainEntityOfPage: {
      "@type": "WebPage",
      "@id": `https://wagenhofer.capital/insights/${article.slug}`,
    },
  };

  return (
    <div className="bg-black text-white min-h-screen flex flex-col">
      <SEO
        title={`${article.title} | Wagenhofer Capital Insights`}
        description={article.excerpt}
        path={`/insights/${article.slug}`}
        image={article.image}
        type="article"
        jsonLd={jsonLd}
      />
      <Navigation />
      <Breadcrumbs
        items={[
          { label: "Insights", to: "/insights" },
          { label: article.title, to: `/insights/${article.slug}` },
        ]}
      />

      <article data-testid="insight-article">
        <header className="container-x pt-12 pb-16 md:pt-16 md:pb-20 border-b border-white/5">
          <div className="max-w-4xl">
            <div className="reveal flex items-center gap-4 mb-6 text-xs text-zinc-500 tracking-[0.2em] uppercase">
              <span className="text-[#D4AF37]">{article.category}</span>
              <span>·</span>
              <span>{article.readingTime}</span>
              <span>·</span>
              <span>{formatDate(article.date)}</span>
            </div>
            <h1 className="reveal delay-1 font-display text-3xl md:text-5xl lg:text-6xl tracking-tighter font-medium leading-[1.04] mb-8">
              {article.title}
            </h1>
            <p className="reveal delay-2 text-zinc-300 text-base md:text-lg leading-relaxed font-light max-w-3xl">
              {article.excerpt}
            </p>
          </div>
        </header>

        <div className="container-x pt-12 md:pt-16">
          <div className="aspect-[16/9] overflow-hidden border border-white/5 reveal">
            <img
              src={article.image}
              alt={article.title}
              title={article.title}
              loading="lazy"
              className="w-full h-full object-cover grayscale"
            />
          </div>
        </div>

        <div className="container-x py-16 md:py-24">
          <div className="max-w-3xl mx-auto space-y-6 text-zinc-300 text-base md:text-lg leading-[1.75] font-light reveal delay-1">
            {article.body.map((p, idx) => (
              <p key={idx}>{p}</p>
            ))}
            <div className="pt-10 mt-10 border-t border-white/10">
              <Link
                to="/insights"
                data-testid="article-back"
                className="inline-flex items-center gap-2 text-xs tracking-[0.28em] uppercase text-zinc-400 hover:text-[#D4AF37] transition-colors"
              >
                <ArrowLeft className="w-3.5 h-3.5" strokeWidth={1.5} />
                Alle Insights
              </Link>
            </div>
          </div>
        </div>
      </article>

      {related.length > 0 && (
        <section className="border-t border-white/5 section-y bg-[#070707]">
          <div className="container-x">
            <div className="overline mb-10">Weitere Beiträge</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-white/5">
              {related.map((a) => (
                <Link
                  key={a.slug}
                  to={`/insights/${a.slug}`}
                  className="group bg-black hover:bg-[#0a0a0a] transition-colors duration-500 p-8 md:p-10"
                >
                  <div className="text-xs text-[#D4AF37] tracking-[0.2em] uppercase mb-4">
                    {a.category}
                  </div>
                  <h3 className="font-display text-xl md:text-2xl tracking-tight font-medium mb-3 leading-snug">
                    {a.title}
                  </h3>
                  <p className="text-zinc-400 leading-relaxed font-light text-sm mb-6">
                    {a.excerpt}
                  </p>
                  <span className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.24em] text-[#D4AF37]">
                    Lesen
                    <ArrowUpRight className="w-3.5 h-3.5" strokeWidth={1.5} />
                  </span>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      <Footer />
    </div>
  );
}
